import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { WorkflowStatus } from '@/api/mastersApi';
import { ChevronDown } from 'lucide-react';

interface StatusDropdownProps {
  value: string | null | undefined;
  options: WorkflowStatus[];
  onChange: (statusId: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md';
  disabled?: boolean;
}

export const StatusDropdown = ({
  value,
  options,
  onChange,
  placeholder = 'Select Status',
  className = '',
  size = 'md',
  disabled = false 
}: StatusDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedStatus = options.find(s => s.id === value);
  
  // Display value: show status name if found, otherwise show placeholder
  // But preserve the value ID if it exists (for when options are still loading)
  const displayText = selectedStatus 
    ? selectedStatus.name.toUpperCase() 
    : value 
      ? '...' // Show minimal indicator while options are loading
      : placeholder;

  const sizeClasses = size === 'sm'
    ? {
        button: 'px-2 py-1 text-[11px]',
        chevron: 'w-3.5 h-3.5 ml-1.5',
        option: 'px-2 py-1 text-[11px]'
      }
    : {
        button: 'px-3 py-1.5 text-xs',
        chevron: 'w-4 h-4 ml-2',
        option: 'px-3 py-2 text-xs'
      };

  // Calculate dropdown position to ensure it's fully visible
  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const dropdownHeight = 240; // max-height
      
      // Calculate available space below and above
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      // Determine if dropdown should open above or below
      const openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      
      let top = 0;
      if (openAbove) {
        // Position above the button
        top = buttonRect.top - Math.min(dropdownHeight, spaceAbove - 10);
      } else {
        // Position below the button
        top = buttonRect.bottom + 4;
      }
      
      // Calculate left position (align with button)
      let left = buttonRect.left;
      
      // Ensure dropdown doesn't overflow right edge
      const dropdownWidth = buttonRect.width;
      if (left + dropdownWidth > viewportWidth - 10) {
        left = viewportWidth - dropdownWidth - 10;
      }
      
      // Ensure dropdown doesn't overflow left edge
      if (left < 10) {
        left = 10;
      }
      
      setDropdownPosition({
        top,
        left,
        width: dropdownWidth
      });
    }
  };

  // Update position when opening dropdown
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      
      // Update on scroll or resize
      const handleUpdate = () => {
        if (isOpen) {
          updateDropdownPosition();
        }
      };
      
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isOpen]);

  // Prevent page scroll when dropdown is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      
      return () => {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (statusId: string) => {
    onChange(statusId);
    setIsOpen(false);
  };

  return (
    <>
      <div className={`relative mt-2 ${className}`}>
        {/* Selected value button */}
        <button
          ref={buttonRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            setIsOpen(!isOpen);
          }}
          className={`w-full ${sizeClasses.button} rounded font-medium border border-gray-300 bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-left flex items-center justify-between p-2 text-sm`}
          disabled={disabled}
        >
          <span className="truncate">
            {displayText}
          </span>
          <ChevronDown className={`text-gray-500 transition-transform flex-shrink-0 ${sizeClasses.chevron} ${isOpen ? 'transform rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown panel - Rendered via portal to escape overflow constraints */}
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border border-gray-300 rounded shadow-md"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            maxHeight: '240px',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <div className="flex flex-col gap-1 py-1">
            {/* Status options - Simple and left-aligned */}
            {options.map((status) => (
              <button
                key={status.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(status.id);
                }}
                className={`w-full text-left font-semibold text-gray-600 hover:bg-gray-100 focus:outline-none text-xs p-2 ${
                  value === status.id ? 'bg-gray-50' : ''
                }`}
              >
                {status.name.toUpperCase()}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
